/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Node Shelf Palette
   Searchable categorized node registry. Spawns nodes via domain command.
   ═══════════════════════════════════════════════════════════════ */

import React, { useState } from 'react';
import { Search, Plus, CategoryGlyph } from '../common/Icons';
import { defaultNodeRegistry } from '../../nodes';
import { CreateNodeCommand } from '../../domain/commands';
import { useDomainBridge } from '../../stores/domainBridge';
import { useViewportStore } from '../../stores/viewportStore';
import { useSelectionStore } from '../../stores/selectionStore';
import styles from './NodeShelf.module.css';

export const NodeShelf: React.FC = () => {
  const [search, setSearch] = useState('');
  const { dispatch } = useDomainBridge();
  const { x, y, zoom } = useViewportStore();
  const { selectNode } = useSelectionStore();

  const categories = defaultNodeRegistry.getByCategory();

  const handleSpawnNode = (type: string) => {
    // Spawn in the visible center of the workspace
    // Assuming workspace center around (400, 300) screen coords
    const spawnX = Math.round((400 - x) / zoom + (Math.random() * 40 - 20));
    const spawnY = Math.round((300 - y) / zoom + (Math.random() * 40 - 20));

    const nodeInstance = defaultNodeRegistry.createDefaultInstance(type, {
      x: spawnX,
      y: spawnY,
    });

    if (nodeInstance) {
      dispatch(new CreateNodeCommand(nodeInstance));
      selectNode(nodeInstance.id);
    }
  };

  const matchesSearch = (nodeDef: import('../../nodes').NodeDefinition) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      nodeDef.label.toLowerCase().includes(q) ||
      nodeDef.description.toLowerCase().includes(q) ||
      nodeDef.type.toLowerCase().includes(q) ||
      nodeDef.category.toLowerCase().includes(q) ||
      Boolean(nodeDef.keywords?.some((k) => k.toLowerCase().includes(q)))
    );
  };

  return (
    <aside className={styles.shelfPanel}>
      <div className={styles.searchHeader}>
        <div className={styles.searchInputWrapper}>
          <Search size={14} />
          <input
            className={styles.searchInput}
            placeholder="Search nodes (e.g. Blur, Levels, Dither)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className={styles.categoryList}>
        {Object.entries(categories).map(([cat, nodes]) => {
          const filtered = nodes.filter((n) => matchesSearch(n));

          if (filtered.length === 0) return null;

          return (
            <div key={cat} className={styles.categorySection}>
              <div className={styles.categoryTitle}>
                <CategoryGlyph category={cat} size={12} />
                <span>{cat}</span>
              </div>

              {filtered.map((nodeDef) => (
                <div
                  key={nodeDef.type}
                  className={styles.nodeCard}
                  onClick={() => handleSpawnNode(nodeDef.type)}
                  title={`${nodeDef.label} — ${nodeDef.description}`}
                >
                  <div className={styles.nodeCardLeft}>
                    <div>
                      <div className={styles.nodeLabel}>{nodeDef.label}</div>
                      <div className={styles.nodeDesc}>{nodeDef.description}</div>
                    </div>
                  </div>
                  <Plus size={14} color="var(--text-tertiary)" />
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </aside>
  );
};
